import { DynamicForm, FormConfig } from "@components/DynamicForm/DynamicForm";
import { useNotification } from "@hooks/useNotification";
import { useQuery } from "@hooks/useQuery";
import { useDocumentDetails } from "@modules/ECMS/MAGP/ApprovalManagement/Drilldown.Hooks";
import { decodeHTML } from "@utils/CommonUtils";
import { useEffect } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { ContentWrapper } from "~/Components";
import { useAuth } from "~/Hooks/useAuth";
import { post, put } from "~/Services";
import { centralizedHost } from "~/Utils";
import { appendSasToken } from "../../Imagehelper";
import { fields } from "../constants";
import { usePrivacyPolicyTabOne } from "../TabOne/TabOne.hooks";

const AddEditTabOne = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const { openToast } = useNotification();
  const { user } = useAuth();
  const effectiveId = query.get("editId") || query.get("id") || undefined;

  const { data: editData, isLoading: isLoadingEdit, refetch: refetchEdit } = usePrivacyPolicyTabOne(effectiveId);

  const {
    data: editDataById,
    isLoading: isLoadingDocument,
    refetch: refetchDocument,
  } = useDocumentDetails(query?.get("id") || "");

  useEffect(() => {
    if (query.get("id")) {
      refetchDocument();
    } else if (query.get("editId")) {
      refetchEdit();
    }
  }, [query.get("editId"), query.get("id")]);

  const formConfig: FormConfig = {
    initialValues: {
      title: decodeHTML(editDataById?.payload?.title || editData?.title || ""),
      tab_title_english: decodeHTML(editDataById?.payload?.tab_title_english || editData?.tab_title_english || ""),
      tab_title_malay: decodeHTML(editDataById?.payload?.tab_title_malay || editData?.tab_title_malay || ""),
      tab_title_mandarin: decodeHTML(editDataById?.payload?.tab_title_mandarin || editData?.tab_title_mandarin || ""),
      body: decodeHTML(editDataById?.payload?.body || editData?.body || ""),

      pdf_url: editDataById?.payload?.pdf_url
        ? [
            {
              uid: "-1",
              name: "pdf_url.pdf",
              status: "done",
              url: appendSasToken(editDataById.payload.pdf_url),
            },
          ]
        : editData?.pdf_url
          ? [
              {
                uid: "-1",
                name: "pdf_url.pdf",
                status: "done",
                url: appendSasToken(editData.pdf_url),
              },
            ]
          : "",
      project_airport_id_1: editDataById
        ? decodeHTML(editDataById.payload.project_airport_id_1)
        : editData
          ? decodeHTML(editData?.project_airport_id_1)
          : "",
      published: editDataById?.payload?.published ?? editData?.published ?? false,
    },
    fields: fields.TabOneData,
  };

  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    let imageKeys: any = ["pdf_url"];
    for (const key of imageKeys) {
      const field = data[key];

      try {
        if (field?.[0]?.originFileObj) {
          const formData = new FormData();
          formData.append("upload-file", field[0].originFileObj);
          formData.append("application", "CENTRALIZED");
          const res: any = await post("management/upload-file", formData);
          if (res) data[key] = res.response.data;
        } else if (field?.[0]?.url) {
          const cleanUrl = field[0].url.split("?sp=")[0];
          data[key] = cleanUrl;
        } else if (Array.isArray(field) && field.length === 0) {
          data[key] = "";
        } else if (!field) {
          data[key] = "";
        }
      } catch (error) {
        console.error(`Error handling image field ${key}:`, error);
      }
    }

    const payload = {
      action,
      method,
      module: "Centralized Policies",
      subModule: "TabOneData",
      type: "CENTRALIZED",
      application: "CENTRALIZED",
      reviewNotes: reviewNotes,
      appList: [
        {
          appName: "CENTRALIZED",
          apiUrl: apiUrl,
        },
      ],
      moduleKey: "CENTRALIZED_privacyPolicyTabOne",
      payload: {
        ...(action === "UPDATE" && { id: editData?.id || editDataById?.payload?.id }),
        ...data,
        created_by: action === "UPDATE" ? editData?.created_by || editDataById?.payload?.created_by : user?.email,
        last_modified_by: action === "UPDATE" ? user?.email : null,
      },
      status,
    };

    console.log("Payload submitted:", payload);

    if (query.get("id")) {
      put(`/management/update-document?approvalId=${query.get("id")}`, payload)
        .then((response: any) => {
          openToast({
            content: status == "PENDING" ? "Data Sent for Review" : "Data Saved Successfully",
            type: "success",
          });
          if (response.status === "OK" && status == "PENDING") {
            navigate("/centralized/status-information?activeTab=pending");
          } else {
            navigate("/centralized/status-information?activeTab=draft");
          }
        })
        .catch((err: any) => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    } else {
      post("/management/publish", payload)
        .then((response: any) => {
          openToast({
            content: status == "DRAFT" ? "Data saved successfully" : "Data Sent for Review",
            type: "success",
          });
          if (response.status === "OK" && status == "PENDING") {
            navigate("/centralized/status-information?activeTab=pending");
          } else {
            navigate("/centralized/status-information?activeTab=draft");
          }
        })
        .catch((err: any) => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    }
  };

  return (
    <ContentWrapper
      title={`${query.get("id") || query.get("editId") ? "Edit" : "Add New"} Tab Title `}
      showBack
    >
      <DynamicForm
        key={editData?.id || editDataById?.payload?.id || "create"} // force re-init when data/id changes
        isLoading={isLoadingEdit || isLoadingDocument}
        config={formConfig}
        onSubmit={(data, buttonValue, reviewNotes) =>
          onSubmit(
            data,
            query.get("editId")
              ? `${centralizedHost}/api/privacy-policy/tab-1/${editData?.id}`
              : `${centralizedHost}/api/privacy-policy/tab-1`,
            query.get("editId") ? "UPDATE" : "CREATE",
            query.get("editId") ? "PATCH" : "POST",
            buttonValue === "save" ? "DRAFT" : "PENDING",
            reviewNotes
          )
        }
        defaultReviewNotes="Kindly review this Policies"
      />
    </ContentWrapper>
  );
};

export default AddEditTabOne;
